import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import { useAuth } from "AuthContext";
import { hasExplicitPermission } from "utils/granularPermissions";
import { outlookRequest } from "./api";

export const MAIL_GRANTS = {
  inbox: "outlook_mail.pages.inbox.view", pmteam: "outlook_mail.mailboxes.pmteam.view", bids: "outlook_mail.mailboxes.bids.view",
  body: "outlook_mail.fields.body.view", attachments: "outlook_mail.actions.attachments.download", notifications: "outlook_mail.actions.notifications.view",
};
const OutlookMailContext = createContext(null);
export const useOutlookMail = () => useContext(OutlookMailContext);
export function useOutlookEligibility() {
  const { isAuthReady, isLoggedIn, user, permissions = [] } = useAuth();
  const signature = [...permissions].sort().join("|");
  const grants = Object.fromEntries(Object.entries(MAIL_GRANTS).map(([name, key]) => [name, hasExplicitPermission(permissions, key)]));
  const canView = Boolean(isAuthReady && isLoggedIn && user?.id && (user.actor_type || "user") === "user" && grants.inbox && (grants.pmteam || grants.bids));
  return { canView, grants, actorId: user?.id, signature };
}
export default function OutlookMailProvider({ children }) {
  const { canView, grants, actorId, signature } = useOutlookEligibility();
  const navigate = useNavigate();
  const scope = `${actorId}:${signature}`;
  const [state, setState] = useState({ mailboxes: [], permissions: {}, loading: false, error: "" });
  const [revision, setRevision] = useState(0);
  const [toast, setToast] = useState(null);
  const [connected, setConnected] = useState(false);
  const generationRef = useRef(0);
  const mailboxRequestRef = useRef(null);
  const activityRequestsRef = useRef(new Map());
  const deliveredRef = useRef(new Set());
  const allowedRef = useRef([]);
  const serverPermissionsRef = useRef({});
  const eligibilityRef = useRef({});
  eligibilityRef.current = { canView, grants, actorId };
  const refresh = useCallback(async ({ refreshHistory = true } = {}) => {
    if (!canView) return;
    mailboxRequestRef.current?.abort();
    const controller = new AbortController();
    mailboxRequestRef.current = controller;
    const generation = generationRef.current;
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const payload = await outlookRequest("/mailboxes", { signal: controller.signal });
      if (controller.signal.aborted || generation !== generationRef.current) return;
      const mailboxes = (payload.mailboxes || []).filter((mailbox) => ["pmteam", "bids"].includes(mailbox.key) && eligibilityRef.current.grants[mailbox.key]);
      allowedRef.current = mailboxes;
      serverPermissionsRef.current = payload.permissions || {};
      if (!payload.permissions?.notifications) setToast(null);
      setState({ scope: `${eligibilityRef.current.actorId}:${signature}`, mailboxes, permissions: payload.permissions || {}, loading: false, error: "" });
      if (refreshHistory) setRevision((value) => value + 1);
    } catch (error) {
      if (!controller.signal.aborted && generation === generationRef.current) {
        if ([401, 403, 404].includes(error.status)) allowedRef.current = [];
        setState((current) => ({ ...current, ...([401, 403, 404].includes(error.status) ? { mailboxes: [], permissions: {} } : {}), loading: false, error: error.message }));
      }
    }
  }, [canView, actorId, signature]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    generationRef.current += 1;
    mailboxRequestRef.current?.abort();
    activityRequestsRef.current.forEach((controller) => controller.abort());
    activityRequestsRef.current.clear();
    deliveredRef.current.clear();
    allowedRef.current = [];
    serverPermissionsRef.current = {};
    setToast(null);
    setConnected(false);
    setState({ scope, mailboxes: [], permissions: {}, loading: canView, error: "" });
    if (canView) void refresh();
    return () => {
      generationRef.current += 1;
      mailboxRequestRef.current?.abort();
      activityRequestsRef.current.forEach((controller) => controller.abort());
    };
  }, [canView, actorId, signature, refresh, scope]);
  useEffect(() => {
    if (!canView) return undefined;
    const onRealtime = async (event) => {
      const payload = event.detail || {};
      if (!allowedRef.current.some((mailbox) => String(mailbox.id) === String(payload.mailbox_id))) return;
      setRevision((value) => value + 1);
      const id = payload.notification_id;
      if (!id || !eligibilityRef.current.grants.notifications || !serverPermissionsRef.current.notifications || deliveredRef.current.has(id) || activityRequestsRef.current.has(id)) return;
      const controller = new AbortController();
      activityRequestsRef.current.set(id, controller);
      const generation = generationRef.current;
      try {
        const result = await outlookRequest(`/activity/${encodeURIComponent(id)}`, { signal: controller.signal });
        if (controller.signal.aborted || generation !== generationRef.current) return;
        const notification = result.notification;
        if (!eligibilityRef.current.grants.notifications || !serverPermissionsRef.current.notifications) return;
        if (!notification || notification.meta?.module !== "outlook_mail" || !allowedRef.current.some((mailbox) => String(mailbox.id) === String(notification.meta.mailbox_id))) return;
        deliveredRef.current.add(id);
        if (deliveredRef.current.size > 500) deliveredRef.current.delete(deliveredRef.current.values().next().value);
        window.dispatchEvent(new CustomEvent("echo-event", { detail: { ...notification, user_id: actorId, actor_id: actorId, actor_type: "user", unread_count: result.unread_count, sound: !notification.read } }));
        if (!notification.read && serverPermissionsRef.current.notifications) setToast({ ...notification, __scope: scope });
      } catch (error) {
        if (!controller.signal.aborted && generation === generationRef.current && ([401, 403, 404].includes(error.status))) { setToast(null); void refresh(); }
      } finally {
        if (activityRequestsRef.current.get(id) === controller) activityRequestsRef.current.delete(id);
      }
    };
    const recover = () => {
      if (document.visibilityState === "hidden") return;
      void refresh();
      window.dispatchEvent(new CustomEvent("outlook-mail-recover"));
    };
    const connection = (event) => setConnected(event.detail?.connected === true);
    const offline = () => setConnected(false);
    window.addEventListener("outlook-mail-realtime", onRealtime);
    window.addEventListener("realtime-connected", recover);
    window.addEventListener("outlook-mail-connection", connection);
    window.addEventListener("focus", recover);
    window.addEventListener("online", recover);
    window.addEventListener("offline", offline);
    document.addEventListener("visibilitychange", recover);
    const currentConnection = window.__ECHO__?.connector?.pusher?.connection;
    if (currentConnection) setConnected(currentConnection.state === "connected");
    return () => {
      window.removeEventListener("outlook-mail-realtime", onRealtime);
      window.removeEventListener("realtime-connected", recover);
      window.removeEventListener("outlook-mail-connection", connection);
      window.removeEventListener("focus", recover);
      window.removeEventListener("online", recover);
      window.removeEventListener("offline", offline);
      document.removeEventListener("visibilitychange", recover);
    };
  }, [canView, actorId, signature, refresh, scope]);
  const visibleState = state.scope === scope ? state : { mailboxes: [], permissions: {}, loading: canView, error: state.error };
  const value = { ...visibleState, canView, grants, connected, revision, refresh, actorId, signature,
    canReadBody: Boolean(grants.body && visibleState.permissions.body), canDownloadAttachments: Boolean(grants.attachments && visibleState.permissions.attachments) };
  const currentToast = toast?.__scope === scope && visibleState.permissions.notifications && visibleState.mailboxes.some((box) => String(box.id) === String(toast.meta?.mailbox_id)) ? toast : null;
  return <OutlookMailContext.Provider value={value}>
    {children}
    <Snackbar open={Boolean(currentToast && canView && grants.notifications)} autoHideDuration={7000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
      <Alert severity="info" onClose={() => setToast(null)} action={<Button color="inherit" size="small" onClick={() => { if (currentToast?.url?.startsWith("/outlook-mail/")) navigate(currentToast.url); setToast(null); }}>Open</Button>} sx={{ maxWidth: 430 }}>{currentToast?.message}</Alert>
    </Snackbar>
  </OutlookMailContext.Provider>;
}
OutlookMailProvider.propTypes = { children: PropTypes.node.isRequired };
